import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '@/components/ui/Logo';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { TermsModal } from '@/components/common/TermsModal';
import { ROUTES } from '@/lib/constants/routes';

const CURRENT_YEAR = new Date().getFullYear();

const SUPPORT_EMAIL = 'info@mynextraise.com';

// Split into two sub-columns Figma's own footer uses, rather than one tall
// column - matches the reference layout's proportions. `/partners`, not
// `ROUTES.TRUSTED_PARTNERS` (`/trusted-partners`) - that path is the
// unrelated authenticated in-app Perks page (A-126). "Contact" has no
// dedicated public page in this project - routed to the support mailto, same
// "link to the real destination" treatment as every other item here.
// "Events" removed (MYN-269 follow-up, explicit instruction) - it linked to
// the real in-app Events page, but that page is itself static (no dynamic
// listing behind it yet), so the footer link was removed rather than kept
// pointing at a non-functional destination.
const EXPLORE_LINKS_COL_1 = [
  { label: 'Benefits', to: ROUTES.BENEFITS },
  { label: 'Due Diligence Platform', to: ROUTES.DUE_DILIGENCE_PLATFORM },
  { label: 'Trusted Partners', to: ROUTES.PARTNERS },
] as const;
const EXPLORE_LINKS_COL_2 = [
  { label: 'Investors', to: ROUTES.INVESTORS },
  { label: 'Contact', href: `mailto:${SUPPORT_EMAIL}` },
] as const;

const CONTACT_LINKS = [
  { label: 'Email Us', href: `mailto:${SUPPORT_EMAIL}` },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/company/my-next-raise/' },
] as const;

export function Footer() {
  const [termsOpen, setTermsOpen] = useState(false);

  return (
    <>
    <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} />
    <footer
      className="bg-navy text-white"
      style={{ padding: '50px clamp(16px, 12vw, 240px)' }}
    >
      <div className="max-w-[1312px] mx-auto">
        {/* Top — brand + columns */}
        <div
          className="flex flex-col md:flex-row justify-between flex-wrap"
          style={{ gap: 48, paddingBottom: 32 }}
        >
          {/* Brand column */}
          <div className="max-w-xs">
            <a href="/" aria-label={`${UI_STRINGS.BRAND_NAME} home`}>
              <Logo variant="white" height={70} aria-hidden />
            </a>
          </div>

          {/* Explore - its own top-level column, not nested with Contact, so
            justify-between's leftover space splits into two normal gaps
            instead of one oversized one (matches the reference layout). */}
          <div className="flex flex-col gap-3">
            <h4
              className="text-white font-semibold uppercase m-0"
              style={{ fontSize: 18, letterSpacing: '0.70px' }}
            >
              Explore
            </h4>
            <div className="flex" style={{ gap: 64 }}>
              {[EXPLORE_LINKS_COL_1, EXPLORE_LINKS_COL_2].map((column, i) => (
                <ul key={i} className="list-none m-0 p-0 flex flex-col gap-3">
                  {column.map((item) => (
                    <li key={item.label}>
                      {'to' in item ? (
                        <Link
                          to={item.to}
                          className="text-slate hover:text-white transition-colors"
                          style={{ fontSize: 16, fontWeight: 400 }}
                        >
                          {item.label}
                        </Link>
                      ) : (
                        <a
                          href={item.href}
                          className="text-slate hover:text-white transition-colors"
                          style={{ fontSize: 16, fontWeight: 400 }}
                        >
                          {item.label}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div className="flex flex-col gap-3">
            <h4
              className="text-white font-semibold uppercase m-0"
              style={{ fontSize: 18, letterSpacing: '0.70px' }}
            >
              Contact
            </h4>
            <ul className="list-none m-0 p-0 flex flex-col gap-3">
              {CONTACT_LINKS.map(({ label, href }) => (
                <li key={label}>
                  <a
                    href={href}
                    className="text-link-blue font-semibold hover:opacity-80 transition-opacity"
                    style={{ fontFamily: 'var(--font-sans)', fontSize: 16 }}
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Divider */}
        <hr className="border-none h-px bg-white/10 m-0" />

        {/* Bottom — copyright + T&C */}
        <div
          className="flex flex-col sm:flex-row justify-between items-center flex-wrap"
          style={{ gap: 16, paddingTop: 32 }}
        >
          <p
            className="text-white/40 m-0"
            style={{ fontSize: 16, fontWeight: 400 }}
          >
            © {CURRENT_YEAR} {UI_STRINGS.BRAND_NAME}. All rights reserved.
          </p>
          <button
            type="button"
            onClick={() => setTermsOpen(true)}
            className="text-white/40 hover:text-white/70 transition-colors"
            style={{ fontSize: 16, fontWeight: 400 }}
          >
            Terms &amp; Conditions
          </button>
        </div>
      </div>
    </footer>
    </>
  );
}
